import * as React from 'react';
import {Link} from 'react-router-dom';
import {inboxCardSurface} from 'src/InboxCard';
import {cn} from 'src/lib/utils';
import type {InboxBacklogSummaryQuery} from '@transactions/graphql';

type Summary = InboxBacklogSummaryQuery['inboxBacklogSummary'];

const compactCount = (n: number): string =>
  n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k` : String(n);

const formatYearRange = (summary: Summary): string => {
  const oldest = summary.backlogOldestDate?.slice(0, 4);
  const newest = summary.backlogNewestDate?.slice(0, 4);
  if (!oldest || !newest) return '';
  return oldest === newest ? oldest : `${oldest}–${newest}`;
};

// Quiet summary of the historical pile of unallocated raw transactions that
// predates the recent era (90 days before Jan 1 of the current year). The
// review card counts only recent items; this card is the single
// representation of everything older — its bars and progress numbers ignore
// the recent era entirely — so the backlog stops masquerading as fresh
// to-do items.
export const InboxBacklogCard = ({summary}: {summary: Summary}) => {
  if (summary.backlogCount === 0) {
    return null;
  }

  // Each backlog year gets a labeled bar (mobile-friendly — no tooltips):
  // the faint track is that year's total raw transactions, the solid overlay
  // is what is still unreviewed, with the remaining count and year below.
  const maxYearTotal = Math.max(...summary.years.map((y) => y.total));
  const processedPercent =
    summary.totalCount > 0
      ? Math.round((summary.processedCount / summary.totalCount) * 100)
      : 0;

  return (
    <div className={cn(inboxCardSurface, 'p-4')}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-foreground text-sm font-semibold">Backlog</span>
        <span className="text-muted-foreground text-xs">
          {summary.backlogCount.toLocaleString('en-US')} items ·{' '}
          {formatYearRange(summary)}
        </span>
      </div>
      <div className="mt-3 flex items-end gap-[3px]">
        {summary.years.map((bucket) => (
          <div
            key={bucket.year}
            className="flex min-w-0 flex-1 flex-col items-center gap-0.5"
          >
            <div className="relative flex h-16 w-full items-end">
              <div
                className="bg-muted-foreground/25 min-h-[3px] w-full rounded-t-sm"
                style={{height: `${(bucket.total / maxYearTotal) * 100}%`}}
              />
              {/* Only draw the solid overlay when something is still
                  unreviewed. A forced minimum height on a zero count would
                  paint the same stub as a year with a handful left, hiding
                  which years are actually done — the eye should be able to
                  scan for the dark caps and find the outstanding years. */}
              {bucket.unallocated > 0 && (
                <div
                  className="bg-muted-foreground/80 absolute bottom-0 min-h-[3px] w-full rounded-t-sm"
                  style={{
                    height: `${(bucket.unallocated / maxYearTotal) * 100}%`,
                  }}
                />
              )}
            </div>
            <span
              className={`text-[9px] leading-tight tabular-nums ${
                bucket.unallocated > 0
                  ? 'text-foreground'
                  : 'text-muted-foreground/40'
              }`}
            >
              {compactCount(bucket.unallocated)}
            </span>
            <span className="text-muted-foreground text-[9px] leading-tight">
              &rsquo;{String(bucket.year).slice(2)}
            </span>
          </div>
        ))}
      </div>
      <div className="text-muted-foreground mt-2 flex items-center gap-3 text-[10px]">
        <span className="flex items-center gap-1">
          <span className="bg-muted-foreground/80 inline-block size-2 rounded-[2px]" />
          unreviewed
        </span>
        <span className="flex items-center gap-1">
          <span className="bg-muted-foreground/25 inline-block size-2 rounded-[2px]" />
          total that year
        </span>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="text-muted-foreground text-xs">
          {processedPercent}% of all history processed
        </span>
        <Link
          to="/backlog"
          className="border-input text-foreground hover:bg-accent/30 rounded-md border px-3 py-1.5 text-xs font-medium whitespace-nowrap shadow-xs transition"
        >
          Browse backlog
        </Link>
      </div>
    </div>
  );
};
